import { syncToggleSwitch } from "../toggle-switch";
import { hideHubScreens } from "../game-hub";
import { getSpeedrunPublishedLevelIndices } from "../speedrun-levels";
import { isPlayTransitionEnabled, runPlayTransition, withExitPlayTransition } from "../play-transition";
import type { LocalConfig, LocalGameMode, LocalPlayerSlot } from "./types";
import { LOCAL_DEFAULT_COLORS, LOCAL_DEFAULT_NAMES, LOCAL_GAME_MODES } from "./types";

export type LocalUiHooks = {
    showEl: (el: HTMLElement | null) => void;
    hideEl: (el: HTMLElement | null) => void;
    openModes: () => void;
    playScreenEnterMotion: () => void;
    playModeCardBeep: () => void;
    startLocalGame: (config: LocalConfig) => void;
    startLocalSoccer?: (config: LocalConfig) => void;
    openLocalSandboxConfig: (onDone: () => void) => void;
    isTouchDevice: () => boolean;
};

const MODE_LABELS: Record<LocalGameMode, string> = {
    normal: "NORMAL",
    wasd: "WASD",
    snake: "SNAKE",
    colorrush: "COLOR RUSH",
    counter: "COUNTER",
    heat: "HEAT",
    speedrun: "SPEEDRUN",
    highway: "HIGHWAY",
    sandbox: "SANDBOX",
    soccer: "CURSOR SOCCER",
};

let hooks: LocalUiHooks | null = null;
let draft: LocalConfig = defaultDraft();

function defaultDraft(): LocalConfig {
    return {
        playerCount: 2,
        showUsernames: true,
        showDivisions: false,
        useJoystick: false,
        players: LOCAL_DEFAULT_NAMES.map((name, i) => ({
            name,
            color: LOCAL_DEFAULT_COLORS[i]!,
        })),
        mode: "normal",
        speedrunLevel: 1,
        colorRushBallCount: 125,
    };
}

const localSetupScreen = () => document.getElementById("localSetupScreen");
const localPlayerCountRow = () => document.getElementById("localPlayerCountRow");
const localPlayersFields = () => document.getElementById("localPlayersFields");
const localModeGrid = () => document.getElementById("localModeGrid");
const localSpeedrunPick = () => document.getElementById("localSpeedrunPick");
const localColorRushPick = () => document.getElementById("localColorRushPick");
const localColorRushBallCountInput = () =>
    document.getElementById("localColorRushBallCount") as HTMLInputElement | null;
const localSpeedrunGrid = () => document.getElementById("localSpeedrunGrid");
const localSandboxHint = () => document.getElementById("localSandboxHint");
const localPlayBtn = () => document.getElementById("localPlayBtn");
const localSetupBackBtn = () => document.getElementById("localSetupBackBtn");
const localUsernamesBtn = () => document.getElementById("localUsernamesBtn");
const localDivisionsBtn = () => document.getElementById("localDivisionsBtn");
const localJoystickBtn = () => document.getElementById("localJoystickBtn");

export function refreshLocalModeCardVisibility(): void {
    /* LOCAL hub card visibility is handled in game-hub.ts */
}

export function hideLocalScreens(): void {
    hooks?.hideEl(localSetupScreen());
}

export function isLocalScreenVisible(): boolean {
    const el = localSetupScreen();
    return !!el && !el.classList.contains("hidden");
}

function renderPlayerCountButtons(): void {
    const row = localPlayerCountRow();
    if (!row) return;
    row.querySelectorAll<HTMLButtonElement>("[data-local-count]").forEach((btn) => {
        const n = Number(btn.getAttribute("data-local-count")) as 2 | 3 | 4;
        btn.classList.toggle("green", draft.playerCount === n);
    });
}

function renderPlayerFields(): void {
    const wrap = localPlayersFields();
    if (!wrap) return;
    wrap.replaceChildren();
    for (let i = 0; i < draft.playerCount; i++) {
        const slot = draft.players[i] ?? {
            name: LOCAL_DEFAULT_NAMES[i]!,
            color: LOCAL_DEFAULT_COLORS[i]!,
        };
        draft.players[i] = slot;
        const row = document.createElement("div");
        row.className = "localPlayerFieldRow";
        row.innerHTML = `
            <label class="localPlayerFieldLabel">Player ${i + 1}</label>
            <input type="text" class="localPlayerNameInput" maxlength="16" autocomplete="off" spellcheck="false" value="${escapeAttr(slot.name)}">
            <input type="color" class="localPlayerColorInput" value="${escapeAttr(slot.color)}" aria-label="Player ${i + 1} color">
        `;
        const nameInput = row.querySelector<HTMLInputElement>(".localPlayerNameInput")!;
        const colorInput = row.querySelector<HTMLInputElement>(".localPlayerColorInput")!;
        nameInput.addEventListener("input", () => {
            slot.name = nameInput.value.trim() || LOCAL_DEFAULT_NAMES[i]!;
        });
        colorInput.addEventListener("input", () => {
            slot.color = colorInput.value || LOCAL_DEFAULT_COLORS[i]!;
        });
        wrap.appendChild(row);
    }
}

function escapeAttr(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function renderModeGrid(): void {
    const grid = localModeGrid();
    if (!grid) return;
    if (grid.dataset.built === "1") {
        grid.querySelectorAll<HTMLElement>(".localModeCard").forEach((card) => {
            const mode = card.getAttribute("data-local-mode") as LocalGameMode | null;
            card.classList.toggle("localModeCard--selected", mode === draft.mode);
        });
        return;
    }
    grid.dataset.built = "1";
    grid.replaceChildren();
    for (const mode of LOCAL_GAME_MODES) {
        const card = document.createElement("div");
        card.className = "modeBox localModeCard mpModeCard";
        card.setAttribute("data-local-mode", mode);
        card.innerHTML = `<h3>${MODE_LABELS[mode]}</h3>`;
        if (mode === draft.mode) card.classList.add("localModeCard--selected");
        grid.appendChild(card);
    }
}

function buildLocalSpeedrunGrid(): void {
    const grid = localSpeedrunGrid();
    if (!grid) return;
    const indices = getSpeedrunPublishedLevelIndices();
    const tag = indices.join(",");
    if (grid.dataset.built === tag) {
        grid.querySelectorAll<HTMLButtonElement>("button").forEach((btn) => {
            const m = /^Level (\d+)$/.exec(btn.textContent ?? "");
            if (!m) return;
            btn.classList.toggle("green", Number(m[1]) === draft.speedrunLevel);
        });
        return;
    }
    grid.dataset.built = tag;
    grid.replaceChildren();
    for (const lvl of indices) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "btn" + (lvl === draft.speedrunLevel ? " green" : "");
        b.textContent = `Level ${lvl}`;
        b.onclick = () => {
            hooks?.playModeCardBeep();
            draft.speedrunLevel = lvl;
            buildLocalSpeedrunGrid();
        };
        grid.appendChild(b);
    }
}

function syncSetupVisibility(): void {
    const speedrunOn = draft.mode === "speedrun";
    const sandboxOn = draft.mode === "sandbox";
    const colorRushOn = draft.mode === "colorrush";
    localSpeedrunPick()?.classList.toggle("hidden", !speedrunOn);
    localColorRushPick()?.classList.toggle("hidden", !colorRushOn);
    localSandboxHint()?.classList.toggle("hidden", !sandboxOn);
    const colorRushInput = localColorRushBallCountInput();
    if (colorRushInput) colorRushInput.value = String(draft.colorRushBallCount);
    if (speedrunOn) buildLocalSpeedrunGrid();
    renderModeGrid();
    syncToggleSwitch(localUsernamesBtn(), draft.showUsernames);
    syncToggleSwitch(localDivisionsBtn(), draft.showDivisions);
    syncToggleSwitch(localJoystickBtn(), draft.useJoystick);
    localJoystickBtn()?.closest(".settingsCard")?.classList.toggle("hidden", draft.mode === "wasd");
    renderPlayerCountButtons();
    renderPlayerFields();
}

export function openLocalSetup(): void {
    if (!hooks) return;
    draft = defaultDraft();
    syncSetupVisibility();
    hooks.hideEl(document.getElementById("modeScreen"));
    hideHubScreens();
    hooks.showEl(localSetupScreen());
    hooks.showEl(document.getElementById("returnBtn"));
    hooks.playScreenEnterMotion();
    hooks.updateMusicState?.();
}

function backToModes(): void {
    hooks?.hideEl(localSetupScreen());
    withExitPlayTransition(() => {
        hooks?.openModes();
    });
}

function setPlayerCount(n: 2 | 3 | 4): void {
    hooks?.playModeCardBeep();
    draft.playerCount = n;
    syncSetupVisibility();
}

function selectMode(mode: LocalGameMode): void {
    hooks?.playModeCardBeep();
    draft.mode = mode;
    if (draft.mode === "wasd") draft.useJoystick = false;
    syncSetupVisibility();
}

function collectPlayers(): LocalPlayerSlot[] {
    const out: LocalPlayerSlot[] = [];
    const wrap = localPlayersFields();
    if (!wrap) return draft.players.slice(0, draft.playerCount);
    const rows = [...wrap.querySelectorAll<HTMLElement>(".localPlayerFieldRow")];
    for (let i = 0; i < draft.playerCount; i++) {
        const row = rows[i];
        const name =
            row?.querySelector<HTMLInputElement>(".localPlayerNameInput")?.value.trim() ||
            LOCAL_DEFAULT_NAMES[i]!;
        const color =
            row?.querySelector<HTMLInputElement>(".localPlayerColorInput")?.value ||
            LOCAL_DEFAULT_COLORS[i]!;
        out.push({ name, color });
    }
    return out;
}

function startPlay(origin?: { x: number; y: number } | null): void {
    if (!hooks) return;
    hooks.playModeCardBeep();
    const colorRushInput = localColorRushBallCountInput();
    if (colorRushInput) {
        const n = Math.floor(Number(colorRushInput.value) || 125);
        draft.colorRushBallCount = Math.max(25, Math.min(500, n));
    }
    const config: LocalConfig = {
        ...draft,
        players: collectPlayers(),
    };
    if (config.mode === "soccer") {
        hooks.startLocalSoccer?.(config);
        return;
    }
    if (config.mode === "sandbox") {
        hooks.openLocalSandboxConfig(() => {
            hooks?.hideEl(localSetupScreen());
            hooks?.startLocalGame(config);
        });
        return;
    }
    const begin = () => {
        hooks?.hideEl(localSetupScreen());
        hooks?.startLocalGame(config);
    };
    if (isPlayTransitionEnabled()) runPlayTransition(begin, { origin });
    else begin();
}

export function initLocalUi(h: LocalUiHooks & { updateMusicState?: () => void }): void {
    hooks = h;

    localSetupBackBtn()?.addEventListener("click", () => backToModes());
    localPlayBtn()?.addEventListener("click", (e) => startPlay({ x: e.clientX, y: e.clientY }));

    localPlayerCountRow()?.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-local-count]");
        if (!btn) return;
        setPlayerCount(Number(btn.getAttribute("data-local-count")) as 2 | 3 | 4);
    });

    localModeGrid()?.addEventListener("click", (e) => {
        const card = (e.target as HTMLElement).closest<HTMLElement>("[data-local-mode]");
        if (!card) return;
        const mode = card.getAttribute("data-local-mode") as LocalGameMode | null;
        if (mode) selectMode(mode);
    });

    localUsernamesBtn()?.addEventListener("click", () => {
        hooks?.playModeCardBeep();
        draft.showUsernames = !draft.showUsernames;
        syncToggleSwitch(localUsernamesBtn(), draft.showUsernames);
    });

    localDivisionsBtn()?.addEventListener("click", () => {
        hooks?.playModeCardBeep();
        draft.showDivisions = !draft.showDivisions;
        syncToggleSwitch(localDivisionsBtn(), draft.showDivisions);
    });

    localJoystickBtn()?.addEventListener("click", () => {
        if (draft.mode === "wasd") return;
        hooks?.playModeCardBeep();
        draft.useJoystick = !draft.useJoystick;
        syncToggleSwitch(localJoystickBtn(), draft.useJoystick);
    });
}
